import { CSSProperties, ReactElement, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Skeleton, TablePagination, Typography, styled } from "@mui/material";
import { rowsPerPageOptions } from "../../constants/Constants";
import { useTranslation } from "react-i18next";

export interface IColumn {
  label: string;
  accessorKey?: string;
  styles?: CSSProperties;
  Cell?: <T>(item: T, opts?: { rowIndex: number; isHovered: boolean }) => React.ReactNode;
}

interface IProps {
  columns: IColumn[];
  data: any[];
  pagination?: boolean;
  styles?: CSSProperties;
  stickyHeader?: boolean;
  rowsInPage?: number;
  displaySkeleton?: boolean;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[200],
  padding: "12px 16px",
  [`&.${tableCellClasses.head}`]: {
    // color: theme.palette.grey[700],
    backgroundColor: theme.palette.grey[200],
    // fontWeight: 600,
    height: 56,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    height: 56,
    color: theme.palette.grey[800],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

const CustomTable = (props: IProps) => {
  const {
    columns,
    data,
    pagination,
    styles,
    stickyHeader,
    rowsInPage,
    displaySkeleton,
  } = props;

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    if (pagination) {
      const storedValue = localStorage.getItem("tableRowsPerPage");
      return storedValue ? parseInt(storedValue) : rowsInPage || 5;
    }
    return rowsInPage || 5;
  });
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const { t } = useTranslation();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (pagination) {
      localStorage.setItem("tableRowsPerPage", newRowsPerPage.toString());
    }
  };

  const visibleRows = useMemo(
    () =>
      pagination
        ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : data,
    [page, rowsPerPage, data, pagination]
  );

  const getTableHeader = (): ReactElement[] => {
    return columns.map((column: IColumn, index: number) => (
      <StyledTableCell
        key={`cust-header-${column.accessorKey}-${index}`}
        sx={column.styles}
      >
        {t(column.label)}
      </StyledTableCell>
    ));
  };

  const getTableBody = () => {
    if (!data || data.length === 0) {
      return (
        <StyledTableRow>
          <StyledTableCell colSpan={columns.length}>
            <Typography align="center">{t("No records found")}</Typography>
          </StyledTableCell>
        </StyledTableRow>
      );
    }

    return visibleRows.map((item: any, index: number) => (
      <StyledTableRow
        key={`cust-tab-body-${index}`}
        onMouseEnter={() => setHoveredRow(index)}
        onMouseLeave={() => setHoveredRow(null)}
      >
        {columns.map((column: IColumn, cellIndex: number) => {
          let cellContent: React.ReactNode = null;
          if (column.Cell) {
            cellContent = column.Cell(item, { rowIndex: index, isHovered: hoveredRow === index });
          } else if (column.accessorKey) {
            cellContent = t(String(item[column.accessorKey] || ""));
          }
          return (
            <StyledTableCell
              key={`cell-${index}-${cellIndex}`}
              sx={column.styles}
            >
              {cellContent}
            </StyledTableCell>
          );
        })}
      </StyledTableRow>
    ));
  };

  return (
    <Paper 
      sx={{ width: "100%", overflow: "hidden", ...styles }}
      id="custom_table"
      data-test-id="custom_table"
      aria-label="Custom table"
    >
      <TableContainer>
        <Table
          stickyHeader={stickyHeader}
          sx={{
            "& .MuiTableCell-stickyHeader": {
              top: 0,
              zIndex: 0,
            },
          }}
        >
          <TableHead>
            <TableRow>{getTableHeader()}</TableRow>
          </TableHead>
          {displaySkeleton ? (
            [...Array(5)].map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {[...Array(columns?.length)].map((_, colIndex) => (
                  <TableCell component="th" key={colIndex}>
                    <Skeleton animation="wave" variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableBody>{getTableBody()}</TableBody>
          )}
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          id="custom_table_pagination"
          data-test-id="custom_table_pagination"
          aria-label="Table pagination"
        />
      )}
    </Paper>
  );
};

export default CustomTable;
